<script setup lang="ts">
import { PARKING_OPTIONS, type WizardDetails } from '~/composables/useOfferteWizard'

const { details, fieldErrors, clearError, persist } = useOfferteWizard()

type LookupStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error'

const lookupStatus = ref<LookupStatus>('idle')
const lookupError = ref<string>('')
let lookupTimer: ReturnType<typeof setTimeout> | null = null
let lookupSeq = 0

function onInput(name: keyof WizardDetails, value: string) {
  details.value = { ...details.value, [name]: value }
  clearError(name as string)
  persist()
  if (name === 'postcode' || name === 'house_number' || name === 'house_addition') {
    scheduleLookup()
  }
}

function isLikelyPostcode(s: string): boolean {
  return /^\s*[1-9][0-9]{3}\s*[A-Za-z]{2}\s*$/.test(s)
}

function scheduleLookup() {
  if (lookupTimer) clearTimeout(lookupTimer)
  const postcode = details.value.postcode
  const houseNumber = details.value.house_number
  if (!isLikelyPostcode(postcode) || !houseNumber.trim()) {
    lookupStatus.value = 'idle'
    lookupError.value = ''
    return
  }
  lookupTimer = setTimeout(runLookup, 400)
}

async function runLookup() {
  const seq = ++lookupSeq
  lookupStatus.value = 'loading'
  lookupError.value = ''
  try {
    const res = await $fetch<{
      ok: boolean
      error?: string
      address?: { street: string; city: string; house_addition: string | null }
    }>('/api/postcode-lookup', {
      query: {
        postcode: details.value.postcode,
        house_number: details.value.house_number,
        house_addition: details.value.house_addition,
      },
    })
    if (seq !== lookupSeq) return
    if (res.ok && res.address) {
      details.value = {
        ...details.value,
        street: res.address.street || '',
        city: res.address.city || details.value.city,
      }
      lookupStatus.value = 'found'
      persist()
    } else if (res.error === 'not_found') {
      details.value = { ...details.value, street: '' }
      lookupStatus.value = 'not_found'
      persist()
    } else if (res.error === 'not_configured') {
      lookupStatus.value = 'idle'
    } else {
      lookupStatus.value = 'error'
      lookupError.value = res.error || 'unknown'
    }
  } catch (err: unknown) {
    if (seq !== lookupSeq) return
    lookupStatus.value = 'error'
    lookupError.value = err instanceof Error ? err.message : 'request_failed'
  }
}

onBeforeUnmount(() => {
  if (lookupTimer) clearTimeout(lookupTimer)
})

const lookupLine = computed(() => {
  if (lookupStatus.value === 'loading') return 'Adres ophalen…'
  if (lookupStatus.value === 'found' && details.value.street) {
    return `${details.value.street} ${details.value.house_number}${details.value.house_addition ? ' ' + details.value.house_addition : ''}, ${details.value.city}`
  }
  if (lookupStatus.value === 'not_found') return 'Geen adres gevonden bij deze combinatie.'
  if (lookupStatus.value === 'error') return 'Adres ophalen mislukt — vul de plaats handmatig in.'
  return ''
})
</script>

<template>
  <section class="pa-wizard__panel">
    <div class="pa-wizard__panel-head">
      <h2>Je gegevens</h2>
      <p>Zodat we je offerte kunnen opstellen en contact met je kunnen opnemen. We bellen je alleen hierover — geen spam.</p>
    </div>

    <form class="pa-wizard__form" novalidate @submit.prevent>
      <!-- Honeypot — must stay empty -->
      <div class="pa-wizard__honeypot" aria-hidden="true">
        <label>Bedrijf (laat leeg)</label>
        <input
            type="text"
            name="company"
            tabindex="-1"
            autocomplete="off"
            :value="details.company"
            @input="(e) => onInput('company', (e.target as HTMLInputElement).value)"
        >
      </div>

      <div class="pa-wizard__form-row">
        <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.first_name }">
          <label for="wz-first">Voornaam *</label>
          <input
              id="wz-first"
              type="text"
              name="first_name"
              placeholder="Voornaam"
              :value="details.first_name"
              required
              @input="(e) => onInput('first_name', (e.target as HTMLInputElement).value)"
          >
          <span v-if="fieldErrors.first_name" class="pa-wizard__error">{{ fieldErrors.first_name }}</span>
        </div>
        <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.last_name }">
          <label for="wz-last">Achternaam *</label>
          <input
              id="wz-last"
              type="text"
              name="last_name"
              placeholder="Achternaam"
              :value="details.last_name"
              required
              @input="(e) => onInput('last_name', (e.target as HTMLInputElement).value)"
          >
          <span v-if="fieldErrors.last_name" class="pa-wizard__error">{{ fieldErrors.last_name }}</span>
        </div>
      </div>

      <div class="pa-wizard__form-row">
        <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.email }">
          <label for="wz-email">E-mailadres *</label>
          <input
              id="wz-email"
              type="email"
              name="email"
              placeholder="naam@email.nl"
              :value="details.email"
              required
              @input="(e) => onInput('email', (e.target as HTMLInputElement).value)"
          >
          <span v-if="fieldErrors.email" class="pa-wizard__error">{{ fieldErrors.email }}</span>
        </div>
        <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.phone }">
          <label for="wz-phone">Telefoon *</label>
          <input
              id="wz-phone"
              type="tel"
              name="phone"
              placeholder="06 12 34 56 78"
              :value="details.phone"
              required
              @input="(e) => onInput('phone', (e.target as HTMLInputElement).value)"
          >
          <span v-if="fieldErrors.phone" class="pa-wizard__error">{{ fieldErrors.phone }}</span>
        </div>
      </div>

      <div class="pa-wizard__form-row pa-wizard__form-row--address">
        <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.postcode }">
          <label for="wz-postcode">Postcode *</label>
          <input
              id="wz-postcode"
              type="text"
              name="postcode"
              placeholder="1234 AB"
              autocomplete="postal-code"
              :value="details.postcode"
              required
              @input="(e) => onInput('postcode', (e.target as HTMLInputElement).value)"
          >
          <span v-if="fieldErrors.postcode" class="pa-wizard__error">{{ fieldErrors.postcode }}</span>
        </div>
        <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.house_number }">
          <label for="wz-house">Huisnummer *</label>
          <input
              id="wz-house"
              type="text"
              name="house_number"
              placeholder="21"
              :value="details.house_number"
              required
              @input="(e) => onInput('house_number', (e.target as HTMLInputElement).value)"
          >
          <span v-if="fieldErrors.house_number" class="pa-wizard__error">{{ fieldErrors.house_number }}</span>
        </div>
        <div class="pa-wizard__field">
          <label for="wz-house-add">Toevoeging</label>
          <input
              id="wz-house-add"
              type="text"
              name="house_addition"
              placeholder="A"
              :value="details.house_addition"
              @input="(e) => onInput('house_addition', (e.target as HTMLInputElement).value)"
          >
        </div>
      </div>

      <div
          v-if="lookupLine"
          class="pa-wizard__lookup"
          :class="{
            'pa-wizard__lookup--loading': lookupStatus === 'loading',
            'pa-wizard__lookup--found': lookupStatus === 'found',
            'pa-wizard__lookup--warn': lookupStatus === 'not_found' || lookupStatus === 'error',
          }"
      >
        <svg
            v-if="lookupStatus === 'loading'"
            class="pa-wizard__lookup-spin"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        <svg
            v-else-if="lookupStatus === 'found'"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4" />
          <path d="M12 16h.01" />
        </svg>
        <span>{{ lookupLine }}</span>
      </div>

      <div class="pa-wizard__form-row">
        <div class="pa-wizard__field">
          <label for="wz-street">Straat</label>
          <input
              id="wz-street"
              type="text"
              name="street"
              placeholder="Straatnaam"
              autocomplete="address-line1"
              :value="details.street"
              @input="(e) => onInput('street', (e.target as HTMLInputElement).value)"
          >
        </div>
        <div class="pa-wizard__field">
          <label for="wz-city">Plaats</label>
          <input
              id="wz-city"
              type="text"
              name="city"
              placeholder="Bijv. Ede"
              autocomplete="address-level2"
              :value="details.city"
              @input="(e) => onInput('city', (e.target as HTMLInputElement).value)"
          >
        </div>
      </div>

      <div class="pa-wizard__field" :class="{ 'pa-wizard__field--invalid': fieldErrors.parking }">
        <label for="wz-parking">Kunnen wij in de buurt parkeren? *</label>
        <select
            id="wz-parking"
            name="parking"
            :value="details.parking"
            required
            @change="(e) => onInput('parking', (e.target as HTMLSelectElement).value)"
        >
          <option value="">Maak een keuze…</option>
          <option v-for="opt in PARKING_OPTIONS" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <span v-if="fieldErrors.parking" class="pa-wizard__error">{{ fieldErrors.parking }}</span>
        <span class="pa-wizard__field-hint">Onze monteurs komen met bedrijfsbus en materiaal — dit helpt ons de afspraak goed in te plannen.</span>
      </div>
    </form>

    <div class="pa-wizard__trust-line">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      <span>Je gegevens worden veilig verwerkt en uitsluitend gebruikt voor je offerte.</span>
    </div>
  </section>
</template>
